/**
 * PURPOSE: Pestaña Versiones de "Asignar camisetas": versiones de uniformes del torneo como espacios de trabajo
 *   (FTR-0046).
 * RESPONSIBILITIES: Mostrar la versión en uso (la que muestran los partidos) y las demás (origen, creación, última
 *   vez que se guardaron); usar otra versión (confirmación: qué se guarda y qué cambia), crear una nueva copia de la
 *   que está en uso y eliminar una que no esté en uso (nunca V1). Sin versionamiento disponible o activo en el
 *   evento, explica por qué. Es el único lugar donde se gestionan las versiones; el estado lo da useUniformVersions.
 * COLLABORATORS: useUniformVersions.js, uniformVersionsDisplay.js, AssignUniformsModal.jsx.
 * ROUTE: winscore/src/area/ProgramacionArea/UniformVersionsPanel.jsx
 */
import React, { useState } from "react";
import { toast } from "react-hot-toast";

import { getUniformErrorMessage } from "../../admin/AdminCompany/uniformOptions.utils.js";
import {
  activateConfirmText,
  activationResultText,
  createVersionConfirmText,
  deleteConfirmText,
  formatLimaDateTimeShort,
  nextVersionNumber,
  versionTitle,
} from "../../utils/uniformVersionsDisplay.js";

const RELOAD_CODES = new Set(["VERSIONING_DISABLED", "VERSIONING_UNAVAILABLE", "VERSION_CONFLICT"]);

const ConfirmBox = ({ text, confirmLabel, onCancel, onConfirm, busy, tone = "violet", children }) => (
  <div className={`mt-2 rounded-lg p-3 ring-1 ${tone === "rose" ? "bg-rose-50 ring-rose-200" : "bg-violet-50 ring-violet-200"}`}>
    <p className={`text-sm font-semibold ${tone === "rose" ? "text-rose-900" : "text-violet-900"}`}>{text}</p>
    {children}
    <div className="mt-2 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy}
        className={`rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60 ${
          tone === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-violet-700 hover:bg-violet-600"
        }`}
      >
        {busy ? "Procesando…" : confirmLabel}
      </button>
    </div>
  </div>
);

export default function UniformVersionsPanel({ versions, disabled = false, onChanged }) {
  const { data, busy, loading, error } = versions;
  const [pending, setPending] = useState(null);
  const [name, setName] = useState("");
  const active = data.active;
  const nextNumber = nextVersionNumber(data.versions);
  const locked = disabled || busy || loading;

  if (!data.available) {
    return (
      <div role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700 ring-1 ring-slate-200">
        El versionamiento de uniformes todavía no está disponible en este entorno (falta aplicar la actualización de base de
        datos de FTR-0046). La asignación de camisetas funciona igual.
      </div>
    );
  }
  if (!data.eligible || !data.enabled) {
    return (
      <div role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700 ring-1 ring-slate-200">
        {!data.eligible
          ? "Las versiones de uniformes son solo para deportes colectivos."
          : "El versionamiento de uniformes está desactivado en este evento. Actívalo en Ajustes del evento → Versionamiento de uniformes."}
      </div>
    );
  }

  // changesMatches: usar o crear una versión puede cambiar lo que muestran los partidos; eliminar, no.
  const run = async (work, success, { changesMatches = true } = {}) => {
    try {
      const result = await work();
      toast.success(success(result));
      setPending(null);
      if (changesMatches) await onChanged?.(result);
    } catch (err) {
      toast.error(getUniformErrorMessage(err, "No se pudo completar la operación con las versiones."));
      if (RELOAD_CODES.has(err?.response?.data?.code)) versions.reload();
    }
  };

  return (
    <div className="space-y-4">
      <section aria-label="Versión en uso" className="rounded-xl border border-violet-200 bg-violet-50 p-3">
        <p className="text-sm text-violet-900">
          Trabajas en <strong>{versionTitle(active)}</strong>. Todo cambio de uniformes (manual, masivo o con el algoritmo) se
          guarda en esta versión. Cambiar de versión no crea versiones nuevas: lo que tienes queda guardado y los partidos por
          jugarse y no publicados pasan a mostrar la versión elegida.
        </p>
        {pending?.type === "create" ? (
          <ConfirmBox
            text={createVersionConfirmText({ active, nextNumber })}
            confirmLabel={`Crear V${nextNumber}`}
            busy={busy}
            onCancel={() => setPending(null)}
            onConfirm={() =>
              run(
                () => versions.create(name),
                (created) => `Ahora trabajas en ${versionTitle(created)}, copia de V${created?.sourceNumber}. Los partidos no cambiaron.`
              )
            }
          >
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              aria-label="Nombre de la nueva versión"
              placeholder={`Nombre (opcional) · por defecto “Versión ${nextNumber}”`}
              className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              disabled={busy}
            />
          </ConfirmBox>
        ) : (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setName("");
                setPending({ type: "create" });
              }}
              disabled={locked}
              className="rounded-md bg-violet-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-600 disabled:opacity-50"
            >
              Nueva versión (copia de V{active?.number})
            </button>
          </div>
        )}
      </section>

      <section aria-label="Versiones del torneo" className="rounded-xl border border-slate-200">
        <h3 className="border-b border-slate-100 px-3 py-2 text-sm font-bold text-slate-800">Versiones del torneo</h3>
        {error ? (
          <p role="alert" className="px-3 py-3 text-sm text-rose-700">
            {error}
          </p>
        ) : null}
        <ul className="divide-y divide-slate-100">
          {data.versions.map((version) => (
            <li key={version.id} className="px-3 py-2" aria-label={versionTitle(version)}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm">
                    <span className="font-black text-slate-900">V{version.number}</span>
                    <span className="text-slate-700"> · {version.name}</span>
                    {version.active ? (
                      <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-800 ring-1 ring-violet-200">En uso</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-slate-500">
                    {version.originLabel} · creada {formatLimaDateTimeShort(version.createdAt)}
                    {version.createdBy?.name ? ` por ${version.createdBy.name}` : ""}
                    {!version.active && version.updatedAt ? ` · guardada ${formatLimaDateTimeShort(version.updatedAt)}` : ""}
                  </p>
                </div>
                {version.active ? null : (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setPending({ type: "activate", target: version })}
                      disabled={locked}
                      aria-label={`Usar V${version.number}`}
                      className="rounded-md border border-violet-300 bg-white px-2 py-1 text-xs font-semibold text-violet-800 hover:bg-violet-50 disabled:opacity-50"
                    >
                      Usar esta versión
                    </button>
                    {version.number !== 1 ? (
                      <button
                        type="button"
                        onClick={() => setPending({ type: "delete", target: version })}
                        disabled={locked}
                        aria-label={`Eliminar V${version.number}`}
                        className="rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        Eliminar
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
              {pending?.target?.id === version.id && pending.type === "activate" ? (
                <ConfirmBox
                  text={activateConfirmText({ target: version, active, exclusiveStrategy: data.exclusiveStrategy })}
                  confirmLabel={`Usar V${version.number}`}
                  busy={busy}
                  onCancel={() => setPending(null)}
                  onConfirm={() => run(() => versions.activate(version), activationResultText)}
                />
              ) : null}
              {pending?.target?.id === version.id && pending.type === "delete" ? (
                <ConfirmBox
                  tone="rose"
                  text={deleteConfirmText(version)}
                  confirmLabel={`Eliminar V${version.number}`}
                  busy={busy}
                  onCancel={() => setPending(null)}
                  onConfirm={() => run(() => versions.remove(version), () => `Se eliminó ${versionTitle(version)}.`, { changesMatches: false })}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
